import { AlertTriangle, CheckCircle2, XCircle } from "lucide-react";
import { type FormEvent, useState } from "react";

import { ApiError } from "@/api/client";
import { useTeardownClient } from "@/api/hooks";
import type { TeardownResponse } from "@/api/types";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
	clientId: string;
	clientSlug: string;
	clientDisplayName: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onComplete?: (report: TeardownResponse) => void;
}

/** Destructive-action modal. Double-gate: type-to-confirm with the
 * slug + acknowledgement checkbox. Renders a post-teardown report
 * (per-step ok / detail) before closing, so the admin sees what
 * actually happened on Fly + Cloudflare. */
export function TeardownDialog({
	clientId,
	clientSlug,
	clientDisplayName,
	open,
	onOpenChange,
	onComplete,
}: Props) {
	const teardown = useTeardownClient();
	const [slugInput, setSlugInput] = useState("");
	const [acknowledged, setAcknowledged] = useState(false);
	const [report, setReport] = useState<TeardownResponse | null>(null);

	function onClose() {
		setSlugInput("");
		setAcknowledged(false);
		setReport(null);
		teardown.reset();
		onOpenChange(false);
	}

	async function onSubmit(event: FormEvent<HTMLFormElement>) {
		event.preventDefault();
		if (slugInput !== clientSlug || !acknowledged) return;
		try {
			const result = await teardown.mutateAsync({
				clientId,
				confirmation: slugInput,
			});
			setReport(result);
			onComplete?.(result);
		} catch {
			/* surfaced via teardown.error */
		}
	}

	if (!open) return null;

	const canSubmit = slugInput === clientSlug && acknowledged && !teardown.isPending;
	const errorMessage =
		teardown.error instanceof ApiError
			? teardown.error.detail
			: teardown.error
				? "Unable to tear down."
				: null;

	return (
		<div
			className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
			// biome-ignore lint/a11y/useSemanticElements: <dialog> needs imperative showModal API that fights with React state
			role="dialog"
			aria-modal="true"
			aria-labelledby="teardown-title"
		>
			<Card className="w-full max-w-lg border-destructive">
				<CardHeader>
					<CardTitle id="teardown-title" className="flex items-center gap-2 text-destructive">
						<AlertTriangle className="h-5 w-5" aria-hidden />
						{report ? "Teardown complete" : "Destroy this engagement?"}
					</CardTitle>
					<CardDescription>
						{report
							? "Per-step outcome below. Review before closing — the client row is gone; Fly + Cloudflare states are in the report."
							: `This destroys ${clientDisplayName}'s Fly app, Postgres cluster, DNS record, and aggregator row. Data is unrecoverable.`}
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					{report ? (
						<TeardownReportView report={report} onClose={onClose} />
					) : (
						<form className="space-y-4" onSubmit={onSubmit} noValidate>
							<div className="space-y-2">
								<Label htmlFor="teardown-slug-confirm">
									Type <span className="font-mono font-semibold">{clientSlug}</span> to confirm
								</Label>
								<Input
									id="teardown-slug-confirm"
									autoComplete="off"
									value={slugInput}
									onChange={(e) => setSlugInput(e.target.value)}
									placeholder={clientSlug}
								/>
							</div>
							<label className="flex cursor-pointer items-start gap-2 text-sm">
								<input
									type="checkbox"
									className="mt-0.5 h-4 w-4"
									checked={acknowledged}
									onChange={(e) => setAcknowledged(e.target.checked)}
								/>
								<span>
									I understand this permanently destroys the Fly app, attached Postgres, and DNS
									record. Data cannot be recovered.
								</span>
							</label>
							{errorMessage ? (
								<p className="text-sm text-destructive" role="alert">
									{errorMessage}
								</p>
							) : null}
							<div className="flex justify-end gap-2 pt-2">
								<Button type="button" variant="outline" onClick={onClose}>
									Cancel
								</Button>
								<Button type="submit" variant="destructive" disabled={!canSubmit}>
									{teardown.isPending ? "Tearing down…" : "Destroy engagement"}
								</Button>
							</div>
						</form>
					)}
				</CardContent>
			</Card>
		</div>
	);
}

function TeardownReportView({
	report,
	onClose,
}: {
	report: TeardownResponse;
	onClose: () => void;
}) {
	return (
		<div className="space-y-3">
			<ul className="space-y-1 text-sm">
				{report.steps.map((s) => (
					<li key={s.name} className="flex items-start gap-2">
						{s.ok ? (
							<CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" aria-hidden />
						) : (
							<XCircle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" aria-hidden />
						)}
						<div>
							<div className="font-mono">{s.name}</div>
							{s.detail ? <p className="text-xs text-muted-foreground">{s.detail}</p> : null}
						</div>
					</li>
				))}
			</ul>
			<div className="flex justify-end">
				<Button onClick={onClose}>Done</Button>
			</div>
		</div>
	);
}
